import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PageSeo } from "@/components/PageSeo";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, XCircle, Clock, DollarSign, Sparkles, Home } from "lucide-react";

const EstylingComparison = () => {
  const { t } = useTranslation();
  const { buildPath } = useLocalizedPath();

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <PageSeo />
      <Header />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="bg-gradient-to-b from-primary/5 to-background py-16 sm:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-foreground">
                {t('seoPages.comparison.hero.title')}
              </h1>
              <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
                {t('seoPages.comparison.hero.subtitle')}
              </p>
              <Button asChild size="lg" className="text-lg px-8">
                <Link to={buildPath('/auth')}>{t('seoPages.comparison.cta')}</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Comparison Cards */}
        <section className="py-16 sm:py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="grid md:grid-cols-2 gap-8">
                {/* Traditional Styling */}
                <Card className="border-2">
                  <CardHeader className="text-center pb-4">
                    <Home className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                    <CardTitle className="text-2xl">
                      {t('seoPages.comparison.traditional.title')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-muted-foreground mb-6">
                      {t('seoPages.comparison.traditional.description')}
                    </p>
                    {['cost', 'time', 'logistics', 'flexibility', 'availability'].map((item) => (
                      <div key={item} className="flex items-start gap-3">
                        <XCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">
                          {t(`seoPages.comparison.traditional.cons.${item}`)}
                        </span>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Digital/AI Styling */}
                <Card className="border-2 border-primary">
                  <CardHeader className="text-center pb-4">
                    <Sparkles className="h-12 w-12 mx-auto mb-4 text-primary" />
                    <CardTitle className="text-2xl">
                      {t('seoPages.comparison.digital.title')}
                    </CardTitle>
                    <span className="inline-block bg-primary text-primary-foreground text-sm px-3 py-1 rounded-full">
                      {t('seoPages.comparison.digital.recommended')}
                    </span>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-muted-foreground mb-6">
                      {t('seoPages.comparison.digital.description')}
                    </p>
                    {['cost', 'time', 'easy', 'flexible', 'always'].map((item) => (
                      <div key={item} className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-foreground">
                          {t(`seoPages.comparison.digital.pros.${item}`)}
                        </span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-16 sm:py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-foreground">
                {t('seoPages.comparison.table.title')}
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-4 px-4 font-semibold text-foreground">
                        {t('seoPages.comparison.table.feature')}
                      </th>
                      <th className="text-center py-4 px-4 font-semibold text-muted-foreground">
                        {t('seoPages.comparison.traditional.title')}
                      </th>
                      <th className="text-center py-4 px-4 font-semibold text-primary">
                        {t('seoPages.comparison.digital.title')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {['price', 'time', 'changes', 'availability', 'quality'].map((row) => (
                      <tr key={row} className="border-b border-border">
                        <td className="py-4 px-4 font-medium text-foreground">
                          {t(`seoPages.comparison.table.rows.${row}.label`)}
                        </td>
                        <td className="text-center py-4 px-4 text-muted-foreground">
                          {t(`seoPages.comparison.table.rows.${row}.traditional`)}
                        </td>
                        <td className="text-center py-4 px-4 text-foreground font-medium">
                          {t(`seoPages.comparison.table.rows.${row}.digital`)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        {/* When to choose */}
        <section className="py-16 sm:py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center text-foreground">
                {t('seoPages.comparison.whenToChoose.title')}
              </h2>
              <p className="text-lg text-muted-foreground text-center mb-12">
                {t('seoPages.comparison.whenToChoose.intro')}
              </p>
              <div className="prose prose-lg max-w-none text-muted-foreground">
                <p>{t('seoPages.comparison.whenToChoose.text')}</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 sm:py-24 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {t('seoPages.comparison.finalCta.title')}
            </h2>
            <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
              {t('seoPages.comparison.finalCta.text')}
            </p>
            <Button asChild size="lg" variant="secondary" className="text-lg px-8">
              <Link to={buildPath('/auth')}>{t('seoPages.comparison.cta')}</Link>
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default EstylingComparison;
